// [NEW] Requirement 1: every table also gets drag handles to resize its columns (utils/resizableColumns.js).
import { installColumnResizing, makeResizable } from "./resizableColumns";
// [NEW FILE] Automatic column widths for every table in the PMS.
//
// Without this, the browser squeezes all columns into the page width, so short values break over lines
// ("01 / Jan / 2024", "Human / Resources") while long comments stay narrow.
//
// What it does, for every <table class="data-table"> on the screen:
// - columns whose values are short (dates, IDs, names, statuses, numbers, buttons) never wrap, so each
//   column is exactly as wide as its content;
// - columns with long text (any line longer than LONG_TEXT characters, e.g. comments or descriptions)
//   wrap inside a readable width (see .col-wrap in styles/components.css);
// - if the table is then wider than its box, the widest text columns are allowed to wrap one by one
//   (.col-squeeze) until it fits; dates, statuses, numbers and action buttons still never wrap;
// - if it still does not fit (very small screens), it scrolls sideways (.table-wrap already does this).
// It runs again whenever the page content changes (new rows, next page, filters, pop-ups), because a
// MutationObserver watches the document. It is started once in layouts/AppLayout.jsx.
const LONG_TEXT = 48;

function longestLine(cell) {
  const text = cell.innerText ?? cell.textContent ?? "";
  return text.split("\n").reduce((max, line) => Math.max(max, line.trim().length), 0);
}

export function fitTable(table) {
  if (!table?.tBodies?.length) return;
  table.classList.add("is-auto");
  const rows = Array.from(table.tBodies[0].rows);
  const longest = [];
  rows.forEach((row) => {
    let col = 0;
    Array.from(row.cells).forEach((cell) => {
      if (cell.colSpan === 1) longest[col] = Math.max(longest[col] || 0, longestLine(cell));
      col += cell.colSpan;
    });
  });
  const apply = (row) => {
    let col = 0;
    Array.from(row.cells).forEach((cell) => {
      const wrap = cell.colSpan > 1 || (longest[col] || 0) > LONG_TEXT;
      if (cell.classList.contains("col-wrap") !== wrap) cell.classList.toggle("col-wrap", wrap);
      col += cell.colSpan;
    });
  };
  rows.forEach(apply);
  if (table.tHead) Array.from(table.tHead.rows).forEach(apply);
  makeResizable(table);
  // a table the user resized keeps exactly the widths the user chose
  if (!table.classList.contains("is-resized")) squeezeToFit(table, rows, longest);
  // Text cut with "..." shows its full value on hover.
  table.querySelectorAll(".person-meta").forEach((el) => {
    const cut = el.scrollWidth > el.clientWidth + 1;
    if (cut && !el.title) el.title = el.innerText.replace(/\s*\n\s*/g, " | ");
    else if (!cut && el.title) el.removeAttribute("title");
  });
}

const MIN_SQUEEZE_TEXT = 12; // columns with shorter values (dates, statuses, numbers) are never squeezed

function columnCells(rows, index) {
  return rows.map((row) => {
    let col = 0;
    return Array.from(row.cells).find((cell) => { const hit = col === index && cell.colSpan === 1; col += cell.colSpan; return hit; });
  }).filter(Boolean);
}

// Let the widest text columns wrap, one at a time, until the table fits in its scroll box.
function squeezeToFit(table, rows, longest) {
  const box = table.parentElement;
  if (!box) return;
  const allRows = [...(table.tHead ? Array.from(table.tHead.rows) : []), ...rows];
  allRows.forEach((row) => Array.from(row.cells).forEach((cell) => cell.classList.remove("col-squeeze")));
  table.classList.remove("is-tight");
  const fits = () => table.scrollWidth <= box.clientWidth + 1;
  if (fits()) return;
  const headerCells = table.tHead?.rows[0] ? Array.from(table.tHead.rows[0].cells) : [];
  const columns = longest.map((len, index) => {
    const cells = columnCells(rows, index);
    return { len, index, cells, width: cells[0]?.getBoundingClientRect().width || 0, actions: cells.some((cell) => cell.querySelector(".row-actions")) };
  });
  const byWidth = (a, b) => b.width - a.width;
  // 1st: text columns, widest first. 2nd (last resort): button groups may go onto two lines.
  const text = columns.filter((c) => !c.actions && c.len > MIN_SQUEEZE_TEXT && c.len <= LONG_TEXT
    && !c.cells.some((cell) => cell.querySelector("select, input, textarea"))).sort(byWidth);
  const actions = columns.filter((c) => c.actions).sort(byWidth);
  const cellsOf = (c) => [...c.cells, headerCells[c.index]].filter(Boolean);
  const squeezed = [];
  // Order of steps: wrap text columns -> smaller cell padding -> button groups on two lines.
  const steps = [...text.map((c) => ({ c })), { tight: true }, ...actions.map((c) => ({ c }))];
  for (const step of steps) {
    if (step.tight) table.classList.add("is-tight");
    else { cellsOf(step.c).forEach((cell) => cell.classList.add("col-squeeze")); squeezed.push(step.c); }
    if (fits()) {
      // Second pass: give back one-line layout to any column that no longer needs to wrap
      // (buttons first, then the narrowest text columns).
      for (const s of [...squeezed].reverse()) {
        cellsOf(s).forEach((cell) => cell.classList.remove("col-squeeze"));
        if (!fits()) cellsOf(s).forEach((cell) => cell.classList.add("col-squeeze"));
      }
      return;
    }
  }
  // still too wide (very small screen): the box scrolls sideways
}

export function fitAllTables(root = document) {
  root.querySelectorAll("table.data-table").forEach(fitTable);
}

// Starts watching the page. Returns a function that stops watching.
export function watchTables(root = document.body) {
  if (typeof MutationObserver === "undefined" || !root) return () => {};
  installColumnResizing();
  let frame = null;
  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => { frame = null; fitAllTables(document); });
  };
  // Only content changes are watched (not attributes), so adding our own classes does not loop.
  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true, characterData: true });
  window.addEventListener("resize", schedule);
  schedule();
  return () => {
    observer.disconnect();
    window.removeEventListener("resize", schedule);
    if (frame) window.cancelAnimationFrame(frame);
  };
}
